import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Widget from '../components/Widget';
import PlaceholderWidget from '../components/widgets/PlaceholderWidget';
import { fetchWidgetData, loadSettings, formatRelativeTime } from '../services/api';
import {
  WidgetConfig,
  WidgetData,
  CombinedAlert,
  defaultWidgets,
  placeholderWidgets,
} from '../services/types';

export default function Dashboard() {
  const navigate = useNavigate();
  const [widgets] = useState<WidgetConfig[]>(defaultWidgets);
  const [widgetData, setWidgetData] = useState<Record<string, WidgetData>>({});
  const [allAlerts, setAllAlerts] = useState<CombinedAlert[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(60);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Load settings
  useEffect(() => {
    const settings = loadSettings();
    setRefreshInterval(settings.refreshInterval);
  }, []);

  // Fetch all widget data in PARALLEL for faster loading
  const fetchAll = useCallback(async () => {
    setIsRefreshing(true);
    const settings = loadSettings();
    const enabledWidgets = widgets.filter(w => w.enabled);

    // Fetch all widgets in parallel
    const fetchPromises = enabledWidgets.map(async (widget) => {
      const configuredUrl = settings.widgetUrls[widget.id]?.api;
      const widgetWithUrl = configuredUrl
        ? { ...widget, apiEndpoint: configuredUrl }
        : widget;
      const data = await fetchWidgetData(widgetWithUrl);
      return { widget, data };
    });

    const results = await Promise.all(fetchPromises);

    // Process results
    const newWidgetData: Record<string, WidgetData> = {};
    const alerts: CombinedAlert[] = [];

    for (const { widget, data } of results) {
      newWidgetData[widget.id] = data;

      // Collect alerts from this widget
      for (const alert of data.alerts) {
        alerts.push({
          ...alert,
          source: widget.name,
          sourceId: widget.id,
        });
      }
    }

    // Sort alerts by timestamp (most recent first)
    alerts.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    setWidgetData(newWidgetData);
    setAllAlerts(alerts);
    setLastRefresh(new Date());
    setIsRefreshing(false);
  }, [widgets]);

  // Initial fetch and auto-refresh
  useEffect(() => {
    fetchAll();

    if (autoRefresh) {
      const interval = setInterval(fetchAll, refreshInterval * 1000);
      return () => clearInterval(interval);
    }
  }, [fetchAll, autoRefresh, refreshInterval]);

  // Handle drill-down (navigate to focused view, optionally with a filter)
  const handleDrillDown = (widgetId: string, filter?: string) => {
    if (filter) {
      navigate(`/dashboard/${widgetId}?filter=${encodeURIComponent(filter)}`);
    } else {
      navigate(`/dashboard/${widgetId}`);
    }
  };

  // Render refresh controls into header portal
  const portalTarget = document.getElementById('refresh-controls-portal');
  const refreshControlsPortal = portalTarget ? createPortal(
    <div className="refresh-status">
      <div className="last-refresh">
        <span className="refresh-label">Last Update:</span>
        <span className="refresh-time">
          {formatRelativeTime(lastRefresh.toISOString())}
          {isRefreshing && ' (refreshing...)'}
        </span>
      </div>
      <div className="refresh-controls">
        <label className="toggle-label">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          <span className="toggle-slider"></span>
          Auto
        </label>
        <select
          className="refresh-select"
          value={refreshInterval}
          onChange={(e) => setRefreshInterval(Number(e.target.value))}
          disabled={!autoRefresh}
        >
          <option value={30}>30s</option>
          <option value={60}>1m</option>
          <option value={120}>2m</option>
          <option value={300}>5m</option>
        </select>
        <button
          className="refresh-button"
          onClick={fetchAll}
          disabled={isRefreshing}
        >
          ↻
        </button>
      </div>
    </div>,
    portalTarget
  ) : null;

  return (
    <div className="dashboard">
      {refreshControlsPortal}

      {/* Widget Grid */}
      <div className="widget-grid">
        {widgets.filter(w => w.enabled).map(widget => (
          <Widget
            key={widget.id}
            config={widget}
            data={widgetData[widget.id] || { status: 'loading', lastUpdated: '', stats: [], alerts: [] }}
            onDrillDown={handleDrillDown}
          />
        ))}

        {/* Placeholder widgets for future tools */}
        {placeholderWidgets.map(widget => (
          <PlaceholderWidget
            key={widget.id}
            name={widget.name}
            icon={widget.icon}
          />
        ))}
      </div>

      {/* Combined Alert Feed */}
      <div className="alert-feed">
        <h3>Recent Alerts (All Sources)</h3>
        {allAlerts.length > 0 ? (
          allAlerts.slice(0, 10).map((alert, index) => (
            <div key={index} className={`alert-item alert-${alert.severity}`}>
              <span className="source">{alert.source}</span>
              <span className="message">{alert.message}</span>
              <span className="time">{formatRelativeTime(alert.timestamp)}</span>
            </div>
          ))
        ) : (
          <div className="no-alerts">No recent alerts</div>
        )}
      </div>
    </div>
  );
}
